import React, { useState } from 'react'
import queryString from 'query-string'
import Link from 'components/elements/Link'
import Head from 'next/head'
import Icon from 'components/elements/Icon'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import { CardType, CategoryType } from 'types/deck'
import { DeckResponse } from 'services/apiTypes/deck.types'

import { DeckService } from 'services/deck.service'

import DiffControls from 'components/comparePage/DiffControls'
import PageWrapper from 'components/misc/PageWrapper'
import CardBanner from 'components/misc/CardBanner'
import DiffCard from 'components/comparePage/DiffCard'
import CardComparison from 'components/comparePage/CardComparison'
import ContentBlock from 'components/comparePage/ContentBlock'

import {
  diffDecks,
  makeCardMaps,
  defaultDiffableFields,
  getDiffPercentages,
  DiffableFields,
  sortDiffableByCardName,
} from 'components/comparePage/utils'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'
import { generateDeckUrl, generateSnapshotUrl } from 'utils/deckSeo'

import styles from './compare.module.scss'
import { ARTICLE_DELINEATOR } from 'components/comparePage/ChooseDeckToDiff'

export const DECK_DELINEATOR = 'd_'
export const SNAPSHOT_DELINEATOR = 's_'

type Props = {
  deckOne: DeckResponse
  deckTwo: DeckResponse
}

const DiffPage = ({ deckOne, deckTwo }: Props) => {
  const [deckA, _setDeckA] = useState(deckOne)
  const [deckB, _setDeckB] = useState(deckTwo)

  const router = useRouter()
  const { ignoreSideboard, includeMaybeboards, limitedCardType } = router.query

  const one = `${router.query.one || ''}`
  const two = `${router.query.two || ''}`

  const deckAIsDeck = !!one?.toLowerCase().startsWith(DECK_DELINEATOR)
  const deckBIsDeck = !!two?.toLowerCase().startsWith(DECK_DELINEATOR)

  const deckAIsSnapshot = !!one?.toLowerCase().startsWith(SNAPSHOT_DELINEATOR)
  const deckBIsSnapshot = !!two?.toLowerCase().startsWith(SNAPSHOT_DELINEATOR)

  const diffFields = (
    typeof router.query.diffFields === 'string' ? router.query.diffFields?.split(',') : defaultDiffableFields
  ) as DiffableFields[]

  // prettier-ignore
  const deckACategories = deckA?.categories.reduce((obj, item) => ({ ...obj, [item.name]: item }), {} as Record<string, CategoryType>)
  // prettier-ignore
  const deckBCategories = deckB?.categories.reduce((obj, item) => ({ ...obj, [item.name]: item }), {} as Record<string, CategoryType>)

  const getCards = (card: CardType, categories: Record<string, CategoryType>) => {
    if (
      limitedCardType &&
      !(
        card.types.includes(`${limitedCardType}`) ||
        card.front?.types.includes(`${limitedCardType}`) ||
        card.back?.types.includes(`${limitedCardType}`)
      )
    )
      return false

    if (ignoreSideboard && card.categories[0] === 'Sideboard') return false
    if (!categories[card.categories[0]]) return true // the deck doesn't have this category for whatever reason, just assume it's included in the deck
    if (!includeMaybeboards && !categories[card.categories[0]]?.includedInDeck) return false

    return true
  }

  const { deckACardMap, deckBCardMap } = makeCardMaps(deckA, deckB)

  const [aNotB, bNotA, same, different] = diffDecks(
    Object.values(deckACardMap).filter(card => getCards(card, deckACategories)),
    Object.values(deckBCardMap).filter(card => getCards(card, deckBCategories)),
    diffFields,
  )

  const [matchPercent, diffPercent, slightDiffPercent] = getDiffPercentages(aNotB, bNotA, same, different)

  const percentageDiffs = [matchPercent, diffPercent, slightDiffPercent]

  // Sort all cards by name
  const inANotB = aNotB.sort(sortDiffableByCardName).map(diff => diff.records[0].id)
  const inBNotA = bNotA.sort(sortDiffableByCardName).map(diff => diff.records[0].id)
  const diffCards = different.sort((a, b) => sortDiffableByCardName(a.aDiff, b.aDiff))
  const exactCards = same
    .sort((a, b) => sortDiffableByCardName(a.aDiff, b.aDiff))
    .map(diffable => diffable.aDiff.records[0].id)

  const deckAName = `A: ${deckA.name} ${deckAIsSnapshot ? '(Snapshot)' : ''}`
  const deckBName = `B: ${deckB.name} ${deckBIsSnapshot ? '(Snapshot)' : ''}`

  const title = `Archidekt compare - ${deckA.name} vs. ${deckB.name}`

  return (
    <PageWrapper title={title} description={`Comparison between ${deckA.name} & ${deckB.name}`}>
      <Head>
        <meta property="og:type" content="website" />
        <meta name="twitter:label1" content="In A not B" />
        <meta name="twitter:data1" content={`${inANotB.length}`} />
        <meta name="twitter:label1" content="In B not A" />
        <meta name="twitter:data2" content={`${inBNotA.length}`} />
        <meta name="twitter:label2" content="Exact match" />
        <meta name="twitter:data3" content={`${exactCards.length}`} />
        <meta name="twitter:label3" content="Similar" />
        <meta name="twitter:data4" content={`${diffCards.length}`} />
      </Head>
      <CardBanner
        title="Deck Comparisons"
        src="https://c1.scryfall.com/file/scryfall-cards/art_crop/front/f/5/f57bdaa1-ce8a-4103-8598-fee751e65a53.jpg?01"
        height="200px"
      />
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.deckNames}>
            <span>
              <Link
                href={deckAIsDeck ? generateDeckUrl(deckA.id, deckA.name) : generateSnapshotUrl(deckA.id, deckA.name)}>
                <span className={styles.aSplotch} />
                {deckAName}
              </Link>
              <button className={styles.clearDeck} onClick={() => null}>
                <Icon name="close" />
              </button>
            </span>
            <span>
              <Link
                href={deckBIsDeck ? generateDeckUrl(deckB.id, deckB.name) : generateSnapshotUrl(deckB.id, deckB.name)}>
                <span className={styles.bSplotch} />
                {deckBName}
              </Link>
              <button className={styles.clearDeck} onClick={() => null}>
                <Icon name="close" />
              </button>
            </span>
          </div>

          <div className={styles.barContainer}>
            {!!(percentageDiffs[0] + percentageDiffs[1]) && (
              <div style={{ flex: `${percentageDiffs[0] + percentageDiffs[1]}` }} className={styles.matchingBar} />
            )}
            {!!percentageDiffs[2] && <div style={{ flex: `${percentageDiffs[2]}` }} className={styles.noMatchingBar} />}
          </div>

          <div className={styles.barLegend}>
            <div>
              <span className={styles.matchSplotch} /> Matching{' '}
              {((percentageDiffs[0] + percentageDiffs[1]) * 100).toFixed(2)} %
              <span className={styles.sumPercent}>
                {' '}
                ({(percentageDiffs[0] * 100).toFixed(2)} % Exact + {(percentageDiffs[1] * 100).toFixed(2)} % Similar)
              </span>
            </div>

            <div>
              <span className={styles.noMatchSplotch} /> Not Matching {(percentageDiffs[2] * 100).toFixed(2)} %
            </div>
          </div>

          <DiffControls />

          <ContentBlock
            deckAName={deckAName}
            deckBName={deckBName}
            cards={inANotB.map(cardId => deckACardMap[cardId])}
            name="In A, not B"
            listLength={inANotB.length}
            defaultCollapsed={!inANotB.length}>
            {inANotB.map(cardId => (
              <DiffCard deckA={true} otherDeck={deckB} key={cardId} card={deckACardMap[cardId]} />
            ))}
          </ContentBlock>

          <ContentBlock
            deckAName={deckAName}
            deckBName={deckBName}
            cards={inBNotA.map(cardId => deckBCardMap[cardId])}
            name="In B, not A"
            listLength={inBNotA.length}
            defaultCollapsed={!inBNotA.length}>
            {inBNotA.map(cardId => (
              <DiffCard deckA={false} otherDeck={deckA} key={cardId} card={deckBCardMap[cardId]} />
            ))}
          </ContentBlock>

          <ContentBlock
            deckAName={deckAName}
            deckBName={deckBName}
            name="Exact Match"
            cards={exactCards.map(cardId => deckACardMap[cardId])}
            listLength={exactCards.length}
            defaultCollapsed={true}>
            {exactCards.map(cardId => (
              <DiffCard key={cardId} card={deckACardMap[cardId]} />
            ))}
          </ContentBlock>

          <ContentBlock
            deckAName={deckAName}
            deckBName={deckBName}
            name="Similar"
            listLength={diffCards.length}
            cards={diffCards.map(diff => diff.aDiff.records[0])}
            skipQuantities
            defaultCollapsed={!diffCards.length}
            className={styles.diffCardContainer}>
            {diffCards.map((diff, index) => (
              <CardComparison deckA={deckA} deckB={deckB} key={index} diff={diff} />
            ))}
          </ContentBlock>
        </div>
      </div>
    </PageWrapper>
  )
}

export default DiffPage

export const getServerSideProps: GetServerSideProps<Props> = async context => {
  try {
    const one = `${context.query.one || ''}`
    const two = `${context.query.two || ''}`

    const deckAIsDeck = !!one?.toLowerCase().startsWith(DECK_DELINEATOR)
    const deckBIsDeck = !!two?.toLowerCase().startsWith(DECK_DELINEATOR)

    const deckAIsSnapshot = !!one?.toLowerCase().startsWith(SNAPSHOT_DELINEATOR)
    const deckBIsSnapshot = !!two?.toLowerCase().startsWith(SNAPSHOT_DELINEATOR)

    const deckAIsArticle = !!one?.toLowerCase().startsWith(ARTICLE_DELINEATOR)
    const deckBIsArticle = !!two?.toLowerCase().startsWith(ARTICLE_DELINEATOR)

    if (!one || !two)
      return {
        redirect: {
          destination: `/partialCompare/?${queryString.stringify(context.query)}`,
          permanent: false,
        },
      }

    const deckAId = one?.replace(/\D/g, '')
    const deckBId = two?.replace(/\D/g, '')

    let deckARequest: Promise<DeckResponse | undefined> = Promise.resolve(undefined)
    let deckBRequest: Promise<DeckResponse | undefined> = Promise.resolve(undefined)

    if (deckAId && deckAIsDeck) deckARequest = new DeckService(context).details(deckAId)
    if (deckBId && deckBIsDeck) deckBRequest = new DeckService(context).details(deckBId)

    if (deckAId && deckAIsSnapshot) deckARequest = new DeckService(context).getSnapshot(deckAId)
    if (deckBId && deckBIsSnapshot) deckBRequest = new DeckService(context).getSnapshot(deckBId)

    if (deckAIsArticle) {
      const source = one.split('_')[1]
      const article = one.split('_')[2]
      const list = one.split('_')[3]

      deckARequest = new DeckService(context).getArticleDeck(source, article, list)
    }

    if (deckBIsArticle) {
      const source = two.split('_')[1]
      const article = two.split('_')[2]
      const list = two.split('_')[3]

      deckBRequest = new DeckService(context).getArticleDeck(source, article, list)
    }

    const [deckOne, deckTwo] = await Promise.all([deckARequest, deckBRequest])

    if (!deckOne || !deckTwo) return { notFound: true }

    return {
      props: { deckOne, deckTwo },
    }
  } catch (error) {
    return LOG_ERROR_AND_REDIRECT(error, context)
  }
}
